import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, Link } from 'react-router-dom';
import { Package, Plus, Edit, Trash2, Copy, AlertTriangle } from 'lucide-react';
import { productService } from '../../services/productService';
import { categoryService } from '../../services/categoryService';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import DataTable from '../../components/common/DataTable';
import SearchInput from '../../components/common/SearchInput';
import Select from '../../components/common/Select';
import Badge from '../../components/common/Badge';
import Tooltip from '../../components/common/Tooltip';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import EmptyState from '../../components/common/EmptyState';
import ImageWithFallback from '../../components/common/ImageWithFallback';
import { formatPrice } from '../../utils/priceCalculator';

const PAGE_SIZE = 20;
const PREVIEW_WIDTH = 256;
const PREVIEW_HEIGHT = 280;

const ProductImageCell = ({ product }) => {
  const [coords, setCoords] = useState(null);
  const triggerRef = useRef(null);
  const hasImage = !!product.images?.[0];

  const show = () => {
    if (!hasImage || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const margin = 8;
    const overflowsRight = rect.right + margin + PREVIEW_WIDTH > window.innerWidth;
    const left = overflowsRight ? rect.left - margin - PREVIEW_WIDTH : rect.right + margin;
    let top = rect.top + rect.height / 2 - PREVIEW_HEIGHT / 2;
    top = Math.max(margin, Math.min(top, window.innerHeight - PREVIEW_HEIGHT - margin));
    setCoords({ left, top });
  };

  const hide = () => setCoords(null);

  return (
    <div
      ref={triggerRef}
      className="relative"
      onMouseEnter={show}
      onMouseLeave={hide}
    >
      <div className="w-12 h-12 rounded-md overflow-hidden bg-app-surface-hover cursor-zoom-in">
        <ImageWithFallback
          src={product.images?.[0]}
          alt={product.name}
          fallbackText={product.name}
          className="w-full h-full object-cover"
        />
      </div>
      {coords && hasImage &&
        createPortal(
          <div
            role="tooltip"
            className="fixed z-[70] pointer-events-none"
            style={{ left: coords.left, top: coords.top, width: PREVIEW_WIDTH }}
          >
            <div className="bg-app-surface border border-app-border rounded-lg shadow-2xl p-2">
              <ImageWithFallback
                src={product.images[0]}
                alt={product.name}
                fallbackText={product.name}
                className="w-full h-56 object-contain rounded bg-app-bg"
              />
              <div className="mt-2 text-xs text-app-text">
                <p className="font-semibold truncate">{product.name}</p>
                {product.brand && (
                  <p className="text-app-text-secondary">Marca: {product.brand}</p>
                )}
                <p className="text-app-text-secondary">
                  {product.variations?.length || 0} variação(ões)
                </p>
                {product.images.length > 1 && (
                  <p className="text-app-text-secondary">
                    +{product.images.length - 1} imagem(ns) adicional(is)
                  </p>
                )}
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

const ProductsManagement = () => {
  const navigate = useNavigate();
  const { success, error } = useToast();
  const { currentStore } = useAuth();
  const lowStockThreshold = currentStore?.lowStockThreshold ?? 5;
  const isResellerStore = currentStore?.businessType === 'reseller';

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState({ q: '', categoryId: '', active: '', lowStock: false });
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [duplicating, setDuplicating] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await productService.getAll({
        ...filter,
        includeInactiveVariations: true,
        page,
        limit: PAGE_SIZE
      });
      setRows(data?.rows || []);
      setTotal(data?.count || 0);
    } catch (err) {
      error(err.message || 'Erro ao carregar');
    } finally {
      setLoading(false);
    }
  }, [filter, page, error]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    categoryService.list().then(setCategories).catch(() => {});
  }, []);

  const confirmDelete = async () => {
    if (!toDelete) return;
    setDeleting(true);
    try {
      await productService.delete(toDelete.id);
      success(`Produto "${toDelete.name}" excluído.`);
      setToDelete(null);
      load();
    } catch (err) {
      error(err.message || 'Erro ao excluir');
    } finally {
      setDeleting(false);
    }
  };

  const duplicate = async (p) => {
    setDuplicating(p.id);
    try {
      const copy = await productService.duplicate(p.id);
      success(`Duplicado como "${copy.name}". Revise antes de ativar.`);
      navigate(`/fornecedor/painel/produtos/${copy.id}/editar`);
    } catch (err) {
      error(err.message || 'Erro ao duplicar');
    } finally {
      setDuplicating(null);
    }
  };

  const categoryOptions = useMemo(
    () => [{ value: '', label: 'Todas categorias' }]
      .concat(categories.map((c) => ({ value: String(c.id), label: c.name }))),
    [categories]
  );

  const columns = [
    {
      key: 'image',
      header: '',
      width: 60,
      render: (p) => <ProductImageCell product={p} />,
    },
    {
      key: 'name',
      header: 'Produto',
      render: (p) => (
        <div>
          <p className="font-medium text-app-text">{p.name}</p>
          {p.brand && <p className="text-xs text-app-text-secondary">{p.brand}</p>}
          {p.Category && (
            <p className="text-xs text-app-text-secondary">/{p.Category.slug}</p>
          )}
        </div>
      ),
    },
    {
      key: 'variations',
      header: 'Variações',
      align: 'center',
      render: (p) => (
        <Badge color="neutral" variant="soft" size="sm">
          {p.variations?.length || 0}
        </Badge>
      ),
    },
    {
      key: 'stock',
      header: 'Estoque',
      align: 'center',
      render: (p) => {
        const total = (p.variations || []).reduce((acc, v) => acc + Number(v.stock || 0), 0);
        const isLow = (p.variations || []).some((v) => Number(v.stock) <= lowStockThreshold);
        return (
          <div className="flex items-center justify-center gap-1">
            <span>{total}</span>
            {isLow && (
              <Tooltip content={`Há SKUs com estoque ≤ ${lowStockThreshold}`}>
                <AlertTriangle size={14} className="text-warning" />
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      key: 'minPrice',
      header: 'Valor Venda',
      align: 'right',
      render: (p) => {
        const prices = (p.variations || []).map((v) => parseFloat(v.price || 0));
        if (!prices.length) return <span className="text-app-text-secondary">—</span>;
        const min = Math.min(...prices);
        return <span className="font-semibold">{formatPrice(min)}</span>;
      },
    },
    // Coluna "Valor Revenda" só faz sentido em loja reseller; pra produto sem
    // resellerPrice positivo cai num placeholder "—".
    ...(isResellerStore ? [{
      key: 'minResellerPrice',
      header: 'Valor Revenda',
      align: 'right',
      render: (p) => {
        const prices = (p.variations || [])
          .map((v) => parseFloat(v.resellerPrice || 0))
          .filter((n) => n > 0);
        if (!prices.length) return <span className="text-app-text-secondary">—</span>;
        return <span className="font-semibold">{formatPrice(Math.min(...prices))}</span>;
      },
    }] : []),
    {
      key: 'active',
      header: 'Status',
      render: (p) => (
        <Badge color={p.active ? 'success' : 'neutral'} variant="soft" size="sm">
          {p.active ? 'Ativo' : 'Inativo'}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (p) => (
        <div className="flex gap-1 justify-end" onClick={(e) => e.stopPropagation()}>
          <Button
            size="sm"
            variant="subtle"
            leftIcon={<Edit size={14} />}
            onClick={() => navigate(`/fornecedor/painel/produtos/${p.id}/editar`)}
          >
            Editar
          </Button>
          <Button
            size="sm"
            variant="ghost"
            leftIcon={<Copy size={14} />}
            onClick={() => duplicate(p)}
            loading={duplicating === p.id}
          >
            Duplicar
          </Button>
          <Button
            size="sm"
            variant="danger"
            leftIcon={<Trash2 size={14} />}
            onClick={() => setToDelete(p)}
          >
            Excluir
          </Button>
        </div>
      ),
    },
  ];

  const clearFilters = () => {
    setFilter({ q: '', categoryId: '', active: '', lowStock: false });
    setPage(1);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="text-3xl font-extrabold text-app-text">Produtos</h1>
        <Link to="/fornecedor/painel/produtos/novo">
          <Button leftIcon={<Plus size={16} />}>Novo produto</Button>
        </Link>
      </div>

      <Card className="p-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="md:col-span-2">
            <SearchInput
              value={filter.q}
              onChange={(q) => { setFilter((f) => ({ ...f, q })); setPage(1); }}
              placeholder="Buscar nome, marca ou descrição…"
              debounceMs={300}
            />
          </div>
          <Select
            value={filter.categoryId}
            onChange={(e) => { setFilter((f) => ({ ...f, categoryId: e.target.value })); setPage(1); }}
            options={categoryOptions}
          />
          <Select
            value={filter.active}
            onChange={(e) => { setFilter((f) => ({ ...f, active: e.target.value })); setPage(1); }}
            options={[
              { value: '', label: 'Todos status' },
              { value: 'true', label: 'Apenas ativos' },
              { value: 'false', label: 'Apenas inativos' }
            ]}
          />
        </div>
        <div className="flex items-center gap-2 mt-3 flex-wrap">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={filter.lowStock}
              onChange={(e) => { setFilter((f) => ({ ...f, lowStock: e.target.checked })); setPage(1); }}
            />
            Apenas estoque baixo (≤ {lowStockThreshold})
          </label>
          {(filter.q || filter.categoryId || filter.active || filter.lowStock) && (
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              Limpar filtros
            </Button>
          )}
        </div>
      </Card>

      <DataTable
        columns={columns}
        rows={rows}
        loading={loading}
        total={total}
        page={page}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
        empty={
          <EmptyState
            icon={Package}
            title={filter.q ? 'Nenhum produto bate com a busca' : 'Sem produtos ainda'}
            description={filter.q ? undefined : 'Cadastre o primeiro pra começar a vender.'}
            action={
              !filter.q && (
                <Link to="/fornecedor/painel/produtos/novo">
                  <Button>Criar primeiro produto</Button>
                </Link>
              )
            }
          />
        }
      />

      <ConfirmDialog
        isOpen={!!toDelete}
        onClose={() => !deleting && setToDelete(null)}
        onConfirm={confirmDelete}
        title={`Excluir "${toDelete?.name || ''}"?`}
        message="Essa ação não pode ser desfeita. Variações e imagens serão removidas."
        confirmLabel="Excluir"
        variant="danger"
        loading={deleting}
      />
    </div>
  );
};

export default ProductsManagement;
